import React, { useState, useEffect } from 'react';
import logo from '../TBL Logo White.png';
import './MainNavigation.css';

const MainNavigation = ({ classVersion, onModuleSelect, onBack }) => {
  const [completedModules, setCompletedModules] = useState(new Set());

  // Check which modules have been completed
  useEffect(() => {
    const checkCompletedModules = () => {
      const completed = new Set();
      
      // Check Try Each Wine - has ratings for all wines
      const tryEachWineRatings = sessionStorage.getItem('wineRatings');
      if (tryEachWineRatings) {
        try {
          const ratings = JSON.parse(tryEachWineRatings);
          if (ratings.wine1 !== undefined && ratings.wine2 !== undefined && ratings.wine3 !== undefined) {
            completed.add('try-each-wine');
          }
        } catch (error) {
          console.error('Error parsing try each wine ratings:', error);
        }
      }
      
      // Check Understand Preferences - has preferences saved
      const winePreferences = sessionStorage.getItem('winePreferences');
      if (winePreferences) {
        try {
          const preferences = JSON.parse(winePreferences);
          if (Object.keys(preferences).length > 0) {
            completed.add('understand-preferences');
          }
        } catch (error) {
          console.error('Error parsing wine preferences:', error);
        }
      }
      
      // Check Rate Each Wine - has characteristic ratings
      const wineCharacteristicRatings = sessionStorage.getItem('wineCharacteristicRatings');
      if (wineCharacteristicRatings) {
        try {
          const ratings = JSON.parse(wineCharacteristicRatings);
          if (ratings.acidity && ratings.sweetness && ratings.body) {
            // Check if all wines have been rated for all characteristics above 0
            const allRated = ['wine1', 'wine2', 'wine3'].every(wine => 
              ratings.acidity[wine] !== undefined && 
              ratings.sweetness[wine] !== undefined && 
              ratings.body[wine] !== undefined &&
              ratings.acidity[wine] > 0 && 
              ratings.sweetness[wine] > 0 && 
              ratings.body[wine] > 0
            );
            if (allRated) {
              completed.add('rate-each-wine');
            }
          }
        } catch (error) {
          console.error('Error parsing wine characteristic ratings:', error);
        }
      }
      
      // Check Blending Trials - if final blend exists, blending trials are considered done
      const finalBlendData = sessionStorage.getItem('finalBlendData');
      if (finalBlendData) {
        try {
          const blendData = JSON.parse(finalBlendData);
          if (blendData.blendName) {
            completed.add('blending-trials');
          }
        } catch (error) {
          console.error('Error parsing final blend data:', error);
        }
      }
      
      // Check Final Blend - has blend name and percentages
      if (finalBlendData) {
        try {
          const blendData = JSON.parse(finalBlendData);
          if (blendData.blendName && blendData.percentages) {
            completed.add('final-blend');
          }
        } catch (error) {
          console.error('Error parsing final blend data:', error);
        }
      }
      
      // Check Wine Varietals - has varietals selected
      const wineVarietals = sessionStorage.getItem('wineVarietals');
      if (wineVarietals) {
        try {
          const varietals = JSON.parse(wineVarietals);
          if (Object.keys(varietals).length > 0) {
            completed.add('wine-varietals');
          }
        } catch (error) {
          console.error('Error parsing wine varietals:', error);
        }
      }
      
      setCompletedModules(completed);
    };
    
    checkCompletedModules();
    
    // Listen for storage changes to update completion status
    const handleStorageChange = () => {
      checkCompletedModules();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically in case of same-tab updates
    const interval = setInterval(checkCompletedModules, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);
  const modules = [
    {
      id: 'try-each-wine',
      title: 'TRY EACH WINE',
      description: 'Rate each wine individually on a 0-5 scale',
      icon: '🍷'
    },
    {
      id: 'understand-preferences',
      title: 'UNDERSTAND YOUR PREFERENCES',
      description: 'Discover your taste preferences through our quiz',
      icon: '🧠'
    },
    {
      id: 'rate-each-wine',
      title: 'RATE EACH WINE',
      description: 'Learn to taste and rate wines by characteristics',
      icon: '👃'
    },
    {
      id: 'blending-trials',
      title: 'BLENDING TRIALS',
      description: 'Experiment with guided and recommended blends',
      icon: '🧪'
    },
    {
      id: 'final-blend',
      title: 'FINAL BLEND',
      description: 'Create and name your perfect blend',
      icon: '🏆'
    },
    {
      id: 'wine-varietals',
      title: 'THE WINES YOU ARE USING',
      description: 'Identify the varietals in your wines',
      icon: '🍇'
    }
  ];

  const handleModuleClick = (moduleId) => {
    onModuleSelect(moduleId);
  };

  return (
    <div className={`main-navigation ${classVersion}-theme`}>
      <div className="nav-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Version Selection
        </button>
        <img src={logo} className="nav-logo" alt="TBL Logo" />
        <h1>Wine Blending Class</h1>
        <p className="class-type">
          {classVersion === 'red' ? 'Red Wine' : 'White Wine'} Class
        </p>
        <p className="nav-description">
          Navigate through each module to complete your wine blending journey
        </p>
      </div>

      <div className="modules-grid">
        {modules.map((module, index) => {
          const isCompleted = completedModules.has(module.id);
          return (
            <div
              key={module.id}
              className={`module-card ${isCompleted ? 'completed' : ''}`}
              onClick={() => handleModuleClick(module.id)}
            >
              <div className="module-number">{index + 1}</div>
              {isCompleted && <div className="completion-checkmark">✓</div>}
              <div className="module-icon">{module.icon}</div>
              <h3 className="module-title">{module.title}</h3>
              <p className="module-description">{module.description}</p>
            </div>
          );
        })}
      </div>

      <div className="footer">
        <div className="footer-separator"></div>
        <img src={logo} className="footer-logo" alt="TBL Logo" />
      </div>
    </div>
  );
};

export default MainNavigation;
